import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';

type AdminUser = {
  id: string;
  nombre: string;
  email: string;
  role: string;
  activo: boolean;
};

type FinancialRole = 'administrador_financiero' | 'contador' | 'auditor';

type FinancialAccess = {
  id: string;
  admin_usuario_id: string;
  gimnasio_id: string;
  sucursal_id: string | null;
  rol_financiero: FinancialRole;
  solo_lectura: boolean;
  puede_conciliar: boolean;
  puede_cerrar_periodo: boolean;
  activo: boolean;
  ultimo_ingreso_at: string | null;
  usuarios_admin?: AdminUser | null;
  sucursales?: { nombre: string } | null;
};

type AccessForm = {
  adminId: string;
  branchId: string;
  role: FinancialRole;
  readOnly: boolean;
  canReconcile: boolean;
  canClosePeriod: boolean;
};

const emptyForm: AccessForm = {
  adminId: '',
  branchId: '',
  role: 'contador',
  readOnly: false,
  canReconcile: true,
  canClosePeriod: false,
};

const roleLabels: Record<FinancialRole, string> = {
  administrador_financiero: 'Administrador financiero',
  contador: 'Contador',
  auditor: 'Auditor / solo lectura',
};

const formatDate = (value: string | null) => {
  if (!value) return 'Nunca';
  return new Intl.DateTimeFormat('es-CL', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
};

export default function FinancialAccessSettings({
  gimnasioId,
  sucursales,
  onClose,
}: {
  gimnasioId: string | null;
  sucursales: Array<{ id: string; nombre: string }>;
  onClose: () => void;
}) {
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [accesses, setAccesses] = useState<FinancialAccess[]>([]);
  const [form, setForm] = useState<AccessForm>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!gimnasioId) {
      setLoading(false);
      setError('Selecciona un gimnasio antes de administrar sus accesos financieros.');
      return;
    }
    setLoading(true);
    setError(null);
    const [adminsResult, accessesResult] = await Promise.all([
      supabase
        .from('usuarios_admin')
        .select('id,nombre,email,role,activo')
        .eq('gimnasio_id', gimnasioId)
        .eq('activo', true)
        .order('nombre'),
      supabase
        .from('finanzas_accesos')
        .select('id,admin_usuario_id,gimnasio_id,sucursal_id,rol_financiero,solo_lectura,puede_conciliar,puede_cerrar_periodo,activo,ultimo_ingreso_at,usuarios_admin(id,nombre,email,role,activo),sucursales(nombre)')
        .eq('gimnasio_id', gimnasioId)
        .order('created_at'),
    ]);

    if (accessesResult.error) {
      setError(accessesResult.error.message.includes('finanzas_accesos')
        ? 'La base de permisos financieros todavía no está desplegada.'
        : accessesResult.error.message);
    }
    if (adminsResult.error) setError(adminsResult.error.message);
    setAdmins((adminsResult.data || []) as AdminUser[]);
    setAccesses((accessesResult.data || []) as unknown as FinancialAccess[]);
    setLoading(false);
  }, [gimnasioId]);

  useEffect(() => { void load(); }, [load]);

  const availableAdmins = useMemo(() => admins.filter((admin) =>
    editingId || !accesses.some((access) => access.admin_usuario_id === admin.id && access.activo),
  ), [accesses, admins, editingId]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError(null);
    setSuccess(null);
    setShowForm(true);
  };

  const openEdit = (access: FinancialAccess) => {
    setEditingId(access.id);
    setForm({
      adminId: access.admin_usuario_id,
      branchId: access.sucursal_id || '',
      role: access.rol_financiero,
      readOnly: access.solo_lectura,
      canReconcile: access.puede_conciliar,
      canClosePeriod: access.puede_cerrar_periodo,
    });
    setError(null);
    setSuccess(null);
    setShowForm(true);
  };

  const updateRole = (role: FinancialRole) => {
    if (role === 'auditor') {
      setForm((current) => ({ ...current, role, readOnly: true, canReconcile: false, canClosePeriod: false }));
      return;
    }
    if (role === 'administrador_financiero') {
      setForm((current) => ({ ...current, role, readOnly: false, canReconcile: true, canClosePeriod: true }));
      return;
    }
    setForm((current) => ({ ...current, role, readOnly: false, canReconcile: true, canClosePeriod: false }));
  };

  const save = async () => {
    if (!gimnasioId || !form.adminId) {
      setError('Selecciona el usuario que tendrá acceso.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    const payload = {
      admin_usuario_id: form.adminId,
      gimnasio_id: gimnasioId,
      sucursal_id: form.branchId || null,
      rol_financiero: form.role,
      solo_lectura: form.readOnly,
      puede_conciliar: !form.readOnly && form.canReconcile,
      puede_cerrar_periodo: !form.readOnly && form.canClosePeriod,
      activo: true,
      updated_at: new Date().toISOString(),
    };
    const result = editingId
      ? await supabase.from('finanzas_accesos').update(payload).eq('id', editingId)
      : await supabase.from('finanzas_accesos').insert(payload);
    setSaving(false);
    if (result.error) {
      setError(result.error.message);
      return;
    }
    setSuccess(editingId ? 'Acceso financiero actualizado.' : 'Acceso financiero habilitado.');
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    await load();
  };

  const toggleAccess = async (access: FinancialAccess) => {
    setError(null);
    const { error: updateError } = await supabase
      .from('finanzas_accesos')
      .update({ activo: !access.activo, updated_at: new Date().toISOString() })
      .eq('id', access.id);
    if (updateError) {
      setError(updateError.message);
      return;
    }
    setSuccess(access.activo ? 'Acceso suspendido.' : 'Acceso reactivado.');
    await load();
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-6 py-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Accesos financieros</h3>
          <p className="mt-1 text-sm text-gray-500">Autoriza el ingreso al portal contable sin crear otra cuenta.</p>
        </div>
        <button onClick={onClose} className="rounded-lg p-2 hover:bg-gray-100" aria-label="Cerrar">
          <i className="ri-close-line text-xl text-gray-500" />
        </button>
      </div>

      <div className="max-h-[70vh] overflow-y-auto p-6">
        <div className="mb-5 flex flex-col justify-between gap-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 sm:flex-row sm:items-center">
          <div className="flex gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-white text-emerald-700"><i className="ri-bank-line text-xl" /></span>
            <div>
              <p className="font-semibold text-emerald-900">Misma identidad, permisos separados</p>
              <p className="mt-1 text-sm text-emerald-800">El usuario ingresa con sus credenciales actuales, pero solo verá finanzas si está habilitado aquí.</p>
            </div>
          </div>
          <button onClick={openCreate} disabled={!gimnasioId || loading} className="whitespace-nowrap rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50">
            <i className="ri-user-add-line mr-2" />Habilitar acceso
          </button>
        </div>

        {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
        {success && <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">{success}</div>}

        {showForm && (
          <div className="mb-5 rounded-xl border border-gray-200 bg-gray-50 p-5">
            <div className="grid gap-4 md:grid-cols-3">
              <label className="text-sm font-medium text-gray-700">Usuario
                <select value={form.adminId} disabled={!!editingId} onChange={(event) => setForm((current) => ({ ...current, adminId: event.target.value }))} className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 disabled:bg-gray-100">
                  <option value="">Seleccionar usuario</option>
                  {availableAdmins.map((admin) => <option key={admin.id} value={admin.id}>{admin.nombre} · {admin.email}</option>)}
                </select>
              </label>
              <label className="text-sm font-medium text-gray-700">Rol financiero
                <select value={form.role} onChange={(event) => updateRole(event.target.value as FinancialRole)} className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5">
                  <option value="administrador_financiero">Administrador financiero</option>
                  <option value="contador">Contador</option>
                  <option value="auditor">Auditor / solo lectura</option>
                </select>
              </label>
              <label className="text-sm font-medium text-gray-700">Alcance
                <select value={form.branchId} onChange={(event) => setForm((current) => ({ ...current, branchId: event.target.value }))} className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5">
                  <option value="">Todas las sucursales</option>
                  {sucursales.map((branch) => <option key={branch.id} value={branch.id}>{branch.nombre}</option>)}
                </select>
              </label>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <label className="flex items-center gap-2 rounded-lg border bg-white p-3 text-sm"><input type="checkbox" checked={form.readOnly} onChange={(event) => setForm((current) => ({ ...current, readOnly: event.target.checked, canReconcile: event.target.checked ? false : current.canReconcile, canClosePeriod: event.target.checked ? false : current.canClosePeriod }))} />Solo lectura</label>
              <label className="flex items-center gap-2 rounded-lg border bg-white p-3 text-sm"><input type="checkbox" disabled={form.readOnly} checked={form.canReconcile} onChange={(event) => setForm((current) => ({ ...current, canReconcile: event.target.checked }))} />Puede conciliar</label>
              <label className="flex items-center gap-2 rounded-lg border bg-white p-3 text-sm"><input type="checkbox" disabled={form.readOnly} checked={form.canClosePeriod} onChange={(event) => setForm((current) => ({ ...current, canClosePeriod: event.target.checked }))} />Puede cerrar períodos</label>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => { setShowForm(false); setEditingId(null); }} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700">Cancelar</button>
              <button onClick={() => void save()} disabled={saving} className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{saving ? 'Guardando…' : editingId ? 'Guardar cambios' : 'Habilitar usuario'}</button>
            </div>
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-200">
          <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto] gap-4 bg-gray-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <span>Usuario</span><span>Permiso</span><span>Estado</span>
          </div>
          {loading ? <div className="p-10 text-center text-sm text-gray-500"><i className="ri-loader-4-line mr-2 animate-spin" />Cargando accesos…</div>
            : accesses.length === 0 ? <div className="p-10 text-center text-sm text-gray-500">Todavía no hay usuarios financieros habilitados.</div>
              : accesses.map((access) => (
                <div key={access.id} className="grid grid-cols-1 gap-3 border-t border-gray-100 px-4 py-4 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto] sm:items-center sm:gap-4">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-gray-900">{access.usuarios_admin?.nombre || 'Usuario administrativo'}</p>
                    <p className="truncate text-sm text-gray-500">{access.usuarios_admin?.email}</p>
                    <p className="mt-1 text-xs text-gray-400">Último ingreso: {formatDate(access.ultimo_ingreso_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{roleLabels[access.rol_financiero]}</p>
                    <p className="mt-1 text-xs text-gray-500">{access.sucursales?.nombre || 'Todas las sucursales'} · {access.solo_lectura ? 'Solo lectura' : [access.puede_conciliar && 'Concilia', access.puede_cerrar_periodo && 'Cierra períodos'].filter(Boolean).join(' · ') || 'Lectura'}</p>
                  </div>
                  <div className="flex items-center justify-between gap-2 sm:justify-end">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${access.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{access.activo ? 'Activo' : 'Suspendido'}</span>
                    <button onClick={() => openEdit(access)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100" title="Editar"><i className="ri-edit-line" /></button>
                    <button onClick={() => void toggleAccess(access)} className={`rounded-lg p-2 ${access.activo ? 'text-red-600 hover:bg-red-50' : 'text-green-700 hover:bg-green-50'}`} title={access.activo ? 'Suspender' : 'Reactivar'}><i className={access.activo ? 'ri-user-forbid-line' : 'ri-user-follow-line'} /></button>
                  </div>
                </div>
              ))}
        </div>
      </div>
    </>
  );
}
